import { Cart, EmoNeutral } from "@assets"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { Minus, Plus, Store, Trash2 } from "lucide-react"
import { useMemo } from "react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { CardContent, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import {
  businessPath,
  cartItemsPath,
  marketplaceProductsPath,
  ordersPath,
  productPath,
} from "@/routes"

interface Business {
  id: number
  business_name: string
  neighborhood?: { id: number; name: string }
}

interface Product {
  id: number
  name?: string
  title?: string
  price: number
  sale_price?: number
  category?: { id: number; name: string }
  business: { id: number; name: string }
}

interface CartItem {
  id: number
  product: Product
  business_id: number
  quantity: number
  notes?: string
}

interface TotalsByBusiness {
  subtotal: number
  delivery_fee: number
  minimum_order: number
  total: number
  meets_minimum: boolean
}

interface Props {
  grouped_items: Record<string, CartItem[]>
  businesses: Business[]
  totals: {
    subtotal: number
    delivery_fees: number
    grand_total: number
    business_totals: Record<string, TotalsByBusiness>
    items_count: number
  }
}

interface StockAdjustment {
  cart_item_id: number
  product_id: number
  product_name: string
  requested: number
  available: number
}

const formatPrice = (n: number) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
  }).format(n)

export default function CartIndex({
  grouped_items,
  businesses,
  totals,
}: Props) {
  const page = usePage<{ flash?: { stock_adjustments?: StockAdjustment[] } }>()
  const stockAdjustments = (page.props.flash?.stock_adjustments ?? []).filter(
    Boolean,
  )

  const bizById = useMemo(() => {
    const map = new Map<number, Business>()
    for (const b of businesses) {
      map.set(b.id, b)
    }
    return map
  }, [businesses])

  const itemById = useMemo(() => {
    const map = new Map<number, CartItem>()
    for (const items of Object.values(grouped_items || {})) {
      for (const item of items) {
        map.set(item.id, item)
      }
    }
    return map
  }, [grouped_items])

  const handleUpdateQty = async (item: CartItem, newQty: number) => {
    try {
      if (newQty <= 0) return void handleRemove(item)
      const token =
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content") ?? ""
      const res = await fetch(`${cartItemsPath()}/${item.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({ quantity: newQty }),
      })
      const data = (await res.json()) as {
        message?: string
        errors?: string[]
        cart_count?: number
      }
      if (!res.ok) throw new Error((data?.errors ?? []).join(", "))
      toast.success(data?.message ?? "Cantidad actualizada")
      // Notificar al header para actualizar el contador de carrito
      window.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart_count: data?.cart_count },
        }),
      )
      router.reload()
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "No se pudo actualizar la cantidad"
      toast.error(msg)
    }
  }

  const handleRemove = async (item: CartItem) => {
    try {
      const token =
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content") ?? ""
      const res = await fetch(`${cartItemsPath()}/${item.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          "X-HTTP-Method-Override": "DELETE",
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          _method: "delete",
          product_id: item.product.id,
        }),
      })
      const data = (await res.json()) as {
        message?: string
        errors?: string[]
        cart_count?: number
      }
      if (!res.ok) throw new Error((data?.errors ?? []).join(", "))
      toast.success(data?.message ?? "Producto eliminado del carrito")
      // Notificar al header para actualizar el contador de carrito
      window.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart_count: data?.cart_count },
        }),
      )
      router.reload()
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "No se pudo eliminar el producto"
      toast.error(msg)
    }
  }

  const handleCheckout = (businessId: number) => {
    // Orders new path expects business_id as param; use a GET to /pedidos/new?business_id=...
    router.get(`${ordersPath()}/new`, { business_id: businessId })
  }

  const hasItems = (totals?.items_count ?? 0) > 0

  return (
    <AppLayout>
      <Head title="Carrito" />

      <div className="mx-auto max-w-5xl space-y-4 py-4 md:py-6">
        <div className="flex items-center justify-center px-4">
          <h1 className="text-foreground flex items-center gap-2 text-[21px] font-bold tracking-[0.21px] md:text-3xl">
            <span className="bg-card flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-full p-1">
              <Cart className="text-primary h-8 w-8" />
            </span>
            Tu carrito
          </h1>
        </div>

        {stockAdjustments.length > 0 && (
          <div className="flex w-full flex-col items-center px-4 text-center">
            <CardTitle>No tienen stock suficiente</CardTitle>
            <CardContent className="flex w-full flex-col items-center space-y-3">
              {stockAdjustments.map((adj) => {
                const item = itemById.get(adj.cart_item_id)
                if (!item) return null

                return (
                  <div
                    key={adj.cart_item_id}
                    className="flex w-full flex-col items-center gap-2 sm:flex-row sm:items-center sm:justify-center sm:text-left"
                  >
                    <div className="min-w-0 text-center sm:text-left">
                      <div className="text-muted-foreground flex truncate text-sm">
                        {adj.product_name}
                      </div>
                      <div className="text-muted-foreground text-sm">
                        Pediste {adj.requested}. Disponible: {adj.available}.
                      </div>
                    </div>

                    <div className="flex w-full items-center justify-center gap-2 sm:w-auto">
                      {adj.available > 0 && (
                        <Button
                          variant="secondary"
                          disabled={adj.available <= 0}
                          onClick={() => {
                            void handleUpdateQty(item, adj.available)
                          }}
                        >
                          Ajustar a {adj.available}
                        </Button>
                      )}
                      <Button
                        variant="destructive"
                        onClick={() => {
                          void handleRemove(item)
                        }}
                      >
                        Eliminar
                      </Button>
                    </div>
                  </div>
                )
              })}
            </CardContent>
          </div>
        )}

        {!hasItems && (
          <div
            className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
            style={{ background: "var(--gradient-highlight)" }}
          >
            <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
              <img src={EmoNeutral} alt="Sin productos" />
            </div>

            <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
              Aún no tienes productos en tu carrito
            </h3>
            <div className="mt-4 flex items-center justify-center gap-2">
              <Link href={marketplaceProductsPath()}>
                <Button size="sm">Ir a productos</Button>
              </Link>
            </div>
          </div>
        )}

        {Object.entries(grouped_items || {}).map(([businessIdStr, items]) => {
          const businessId = Number(businessIdStr)
          const biz = bizById.get(businessId)
          const bt = totals?.business_totals?.[businessIdStr]

          return (
            <div key={businessId}>
              <CardTitle className="flex items-center gap-2 px-4 text-base">
                <Store className="h-4 w-4" />
                {biz ? (
                  <Link href={businessPath(biz.id)} className="hover:underline">
                    {biz.business_name}
                  </Link>
                ) : (
                  `Negocio ${businessId}`
                )}
              </CardTitle>
              <CardContent className="bg-card space-y-2 py-4">
                {items.map((item) => (
                  <div key={item.id} className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <Link
                        href={productPath(item.product.id)}
                        className="font-medium hover:underline"
                      >
                        {item.product.name ?? item.product.title}
                      </Link>
                      <div className="text-muted-foreground text-sm">
                        {formatPrice(
                          item.product.sale_price ?? item.product.price,
                        )}{" "}
                        x {item.quantity}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          void handleUpdateQty(item, item.quantity - 1)
                        }}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <div className="w-10 text-center">{item.quantity}</div>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          void handleUpdateQty(item, item.quantity + 1)
                        }}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          void handleRemove(item)
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}

                {bt && (
                  <div className="border-t pt-2 text-sm">
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span className="font-medium">
                        {formatPrice(bt.subtotal)}
                      </span>
                    </div>
                    {bt.delivery_fee > 0 && (
                      <div className="flex justify-between">
                        <span>Envío</span>
                        <span className="font-medium">
                          {formatPrice(bt.delivery_fee)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Total</span>
                      <span className="font-semibold">
                        {formatPrice(bt.total)}
                      </span>
                    </div>
                    {!bt.meets_minimum && (
                      <div className="mt-1 text-xs text-red-600">
                        Pedido mínimo: {formatPrice(bt.minimum_order)}
                      </div>
                    )}

                    <div className="mt-3 flex justify-end">
                      <Button
                        disabled={!bt.meets_minimum}
                        onClick={() => handleCheckout(businessId)}
                        className="w-full sm:w-auto"
                      >
                        Continuar pedido
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </div>
          )
        })}
      </div>
    </AppLayout>
  )
}
